import axios from 'axios'

import { ALL } from '@constants/categories'

import { showToast } from '@services/toast'
import { getApiKey, getUserInfo, getToken, refreshToken } from '@services/auth'
import { valuationStatus } from '@utils/valuationStatus'
import moment from 'moment'

const API_URL = process.env.REACT_APP_API_URL
const VALUATE = `${API_URL}/valuate/internal/homeequity`
const VALUATION_BY_ID = id => `${API_URL}/valuate/public/${id}`
const VALUATIONS = `${API_URL}/valuate/all`
const AMENITIES = `${API_URL}/valuate/resources/amenities`
const ADS_LIST = `${API_URL}/valuate/resources/realties`
const ZONING = cityId => `${API_URL}/valuate/resources/zoning?city_id=${cityId}`
const NEIGHBORHOODS = cityId =>
  `${API_URL}/valuate/resources/neighborhood?city_id=${cityId}`
const CITIES = cityName =>
  `${API_URL}/valuate/resources/cities?name=${cityName}`

function workaroundForPayloadProcessing(params) {
  /* Workaround */
  let filtredValues = params
  let tempValuesBedrooms = []
  let tempValuesSuites = []
  let tempValuesGarages = []

  Object.keys(filtredValues.bedrooms).map(function(el) {
    tempValuesBedrooms.push(parseInt(el, 10))
  })

  Object.keys(filtredValues.suites).map(function(el) {
    tempValuesSuites.push(parseInt(el, 10))
  })

  Object.keys(filtredValues.garages).map(function(el) {
    tempValuesGarages.push(parseInt(el, 10))
  })

  filtredValues.bedrooms = tempValuesBedrooms
  filtredValues.suites = tempValuesSuites
  filtredValues.garages = tempValuesGarages
  /* End of workaround */

  return filtredValues
}

class ValuationService {
  constructor() {
    this.user = getUserInfo()

    this.internalHeaders = {
      'x-api-key': getApiKey(),
    }

    this.externalHeaders = () => ({
      Authorization: getToken(),
    })

    this.id = null
  }

  valuate = async (params, loading) => {
    const payload = this.formatParams(params)
    let id = null

    loading(true)

    try {
      const request = await axios.post(
        VALUATE,
        { data: payload, user_id: this.user['custom:id'] },
        {
          headers: this.internalHeaders,
        },
      )

      if (request.status === 200 && request.data.success) {
        id = request.data.data.id
        showToast('Sua avaliação foi criada com sucesso!')
      } else {
        showToast('Ocorreu um erro ao criar sua avaliação!', 'failure')
      }
    } catch (err) {
      console.error(err)
      this.errorHandler(err)
      showToast('Ocorreu um erro ao criar sua avaliação!', 'failure')
    } finally {
      loading(false)
    }

    return id
  }

  getValuationStatusById = async id => {
    let status = null

    try {
      const request = await axios.get(VALUATION_BY_ID(id), {
        headers: this.externalHeaders(),
      })

      if (request?.status === 200 && request?.data?.success) {
        status = request.data?.data?.status
      }
      if (request?.status === 503) {
        status = 1
      }
    } catch (err) {
      console.error(err)
      this.id = id
      status = 3
      if (err.response.status === 401) {
        status = await this.errorHandler(err, this.getValuationStatusById)
      }
    }

    return status
  }

  getValuationById = async id => {
    let valuation = null

    try {
      const request = await axios.get(VALUATION_BY_ID(id), {
        headers: this.externalHeaders(),
      })

      if (request?.status === 200 && request?.data?.success) {
        const { area, area_percent_margin } = request.data.data?.valuation

        valuation = {
          ...request.data.data?.valuation,
          area: Number(area),
          area_percent_margin: Number(area_percent_margin),
          id,
        }
        valuation.fullAddress = this.formatFullAddress(valuation)
        if (
          valuation.region_census_tract_rating ||
          valuation.region_sqr_mtr_rating
        ) {
          const regionCensusTractRating =
            valuation.region_census_tract_rating >= 0
              ? valuation.region_census_tract_rating
              : 0
          const regionSqrMtrRating =
            valuation.region_sqr_mtr_rating >= 0
              ? valuation.region_sqr_mtr_rating
              : 0
          valuation.region_rating = (
            0.7 * regionSqrMtrRating +
            0.3 * regionCensusTractRating
          ).toFixed(2)
        }
        valuation.ad_sample = valuation.ad_sample?.sort(
          (a, b) => b.similarity - a.similarity,
        )
      }
    } catch (err) {
      console.error(err)
      this.id = id
      valuation = await this.errorHandler(err, this.getValuationById)
    }
    return valuation
  }

  getValuations = async () => {
    let valuations = []
    try {
      const request = await axios.get(VALUATIONS, {
        headers: this.externalHeaders(),
      })

      if (
        request?.data?.code === 200 &&
        request?.data.success &&
        request?.data.data?.length > 0
      )
        valuations = request.data.data.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        )
    } catch (err) {
      console.error(err)
      this.id = null
      valuations = await this.errorHandler(err, this.getValuations)
    }
    return this.formatValuations(valuations)
  }

  getAdSample = async (params, setLoading = () => null) => {
    const payload = this.formatAdSampleParams(params)
    let ads = null

    setLoading(true)

    try {
      const request = await axios.post(
        ADS_LIST,
        { data: payload, user_id: this.user['custom:id'] },
        {
          headers: this.externalHeaders(),
        },
      )

      if (request.status === 200 && request.data?.realties?.length >= 5) {
        ads = {
          params: {
            ...payload,
            fullAddress: this.formatFullAddress(payload),
          },
          baseSimilarity: request?.data?.base_similarity,
          ad_sample: this.formatSample(request.data.realties),
        }
      } else {
        showToast('Amostra insuficiente', 'failure')
      }
    } catch (err) {
      console.error(err)
      this.errorHandler(err)
      showToast('Ocorreu um erro ao requisitar os anúncios', 'failure')
    } finally {
      setLoading(false)
    }

    return ads
  }

  getNeighborhoods = async cityId => {
    let neighborhoods = null

    try {
      const request = await axios.get(NEIGHBORHOODS(cityId), {
        headers: this.externalHeaders(),
      })
      if (request.status === 200) {
        neighborhoods =
          request.data?.length > 0 ? this.formatNeighborhoods(request.data) : []
      }
    } catch (err) {
      console.error(err)
      this.id = cityId
      neighborhoods = await this.errorHandler(err, this.getNeighborhoods)
    }

    return neighborhoods
  }

  getZonings = async cityId => {
    let zonings = null

    try {
      const request = await axios.get(ZONING(cityId), {
        headers: this.externalHeaders(),
      })
      if (request?.status === 200) {
        zonings =
          request.data?.length > 0 ? this.formatZonings(request.data) : []
      }
    } catch (err) {
      console.error(err)
      this.id = cityId
      zonings = await this.errorHandler(err, this.getZonings)
    }

    return zonings
  }

  getCities = async cityName => {
    let cities = null

    try {
      const request = await axios.get(CITIES(cityName), {
        headers: this.externalHeaders(),
      })
      if (request.status === 200) {
        cities = request.data?.length > 0 ? this.formatCities(request.data) : []
      }
    } catch (err) {
      console.error(err)
      this.id = cityName
      cities = await this.errorHandler(err, this.getCities)
    }

    return cities
  }

  getAmenities = async () => {
    let amenities = null

    try {
      const request = await axios.get(AMENITIES, {
        headers: this.externalHeaders(),
      })

      if (request?.status === 200) {
        amenities =
          request.data?.length > 0
            ? request.data
                .map(({ Opcional }) => ({
                  label: Opcional,
                  name: Opcional,
                }))
                .sort((a, b) => ('' + a.label).localeCompare(b.label))
            : []
      }
    } catch (err) {
      console.error(err)
      this.id = null
      amenities = await this.errorHandler(err, this.getAmenities)
    }

    return amenities
  }

  formatValuations = valuations => {
    if (!valuations?.length || valuations[0].createdAt) return valuations
    const formatItem = item => {
      const creationDate = new Date(item.created_at)
      let error

      if (item.error) {
        error =
          item.error === 'no_sample_found'
            ? 'Anúncios insuficientes'
            : 'Erro interno'
      }
      let fullAddress = this.formatFullAddress(item)

      return {
        id: item._id,
        fullAddress,
        addressCity: item.address_city,
        addressDistrict: item.address_district,
        addressNumber: item.address_number,
        addressState: item.address_state,
        addressStreet: item.address_street,
        area: item.area,
        areaPercentMargin: item.area_percent_margin,
        bedrooms: item.bedrooms,
        createdAt: creationDate,
        garages: item.garages,
        latLng: item.lat_lng,
        maxPrice: item.max_price,
        maxSqrMeterPrice: item.max_sqr_meter_price,
        minPrice: item.min_price,
        minSqrMeterPrice: item.min_sqr_meter_price,
        price: item.price,
        pricePercentMargin: item.price_percent_margin,
        radius: item.radius,
        refundable: item.refundable,
        sampleMaxSize: item.sample_max_size,
        sqrMeterPrice: item.sqr_meter_price,
        status: valuationStatus[item.status],
        subCategory: item.sub_category,
        suites: item.suites,
        transactionType: item.transaction_type,
        updatedAt: new Date(item.updated_at),
        year: creationDate.getFullYear(),
        month: creationDate.toLocaleString('pt-BR', {
          month: 'short',
        }),
        monthNumber: Number(creationDate.getMonth()),
        day: creationDate.getDate(),
        error,
      }
    }
    return Array.isArray(valuations)
      ? valuations.map(formatItem)
      : formatItem(valuations)
  }

  formatNeighborhoods = rawNeighborhoods => {
    if (!rawNeighborhoods || rawNeighborhoods[0].id) return rawNeighborhoods
    return rawNeighborhoods.map(item => ({
      id: item.Id,
      area: item.Area,
      cityId: item.CidadeId,
      lat: item.PontoCentralLatitude,
      lng: item.PontoCentralLongitude,
      name: item.Nome,
      path: item.PontosPoligono,
    }))
  }

  formatZonings = rawZonings => {
    if (!rawZonings || rawZonings[0].id) return rawZonings
    return rawZonings.map(item => ({
      path: item.Encode[0],
      cityId: item.CidadeId,
      id: item.Id,
      name: item.Nome || item.Sigla,
    }))
  }

  formatParams = params => {
    const payload = {
      revaluation_ids: params.selectedRealties,
      address_city: params.city,
      address_district: params.neighborhood,
      address_number: params.number,
      address_state: params.uf,
      base_similarity: params.baseSimilarity,
      address_street: params.street,
      area: params.area,
      area_percent_margin: params.areaPercentMargin,
      price_percent_margin: params.pricePercentMargin,
      lat_lng: [params.lat, params.lng],
      sub_category: Object.keys(params.category).map(key => ALL[key].value),
      transaction_type: 'Venda',
      radius: params.radius,
      main_area: String(params.main_area),
      main_sub_category: params.main_sub_category.value,
      amenities:
        (params.amenities && Object.keys(params.amenities)) || undefined,
      geometries:
        (params.geometries?.length > 0 &&
          params.geometries.map(item =>
            item
              .getPath()
              .getArray()
              .map(item => [item.lat(), item.lng()]),
          )) ||
        undefined,
    }

    if (payload.geometries) {
      let fixedGeom = [...payload.geometries]
      if (
        fixedGeom[0][0].toString() !==
        fixedGeom[0][payload.geometries[0].length - 1].toString()
      ) {
        fixedGeom[0].push(fixedGeom[0][0])
      }
      payload.geometries = fixedGeom
    }

    if (params.sqrMeterPrice) payload.sqr_meter_price = params.sqrMeterPrice
    const tempPayload = workaroundForPayloadProcessing(params)
    tempPayload.bedrooms.length !== 0
      ? (payload.bedrooms = tempPayload.bedrooms)
      : null
    tempPayload.suites.length !== 0
      ? (payload.suites = tempPayload.suites)
      : null
    tempPayload.garages.length !== 0
      ? (payload.garages = tempPayload.garages)
      : null
    params.main_bedrooms
      ? (payload.main_bedrooms = String(params.main_bedrooms))
      : null
    params.main_suites
      ? (payload.main_suites = String(params.main_suites))
      : null
    params.main_garages
      ? (payload.main_garages = String(params.main_garages))
      : null

    if (params.alpha && params.beta) {
      payload.proximity_distances = [
        { distance: params.alpha, proximity_level: 'Alpha' },
        { distance: params.beta, proximity_level: 'Beta' },
        { distance: Number.MAX_SAFE_INTEGER, proximity_level: 'Gama' },
      ]
    }

    return payload
  }

  formatAdSampleParams = params => {
    const payload = {
      address_city: params.city,
      address_district: params.neighborhood,
      address_number: params.number,
      address_state: params.uf,
      address_street: params.street,
      main_area: params.main_area,
      area: params.area,
      area_percent_margin: params.areaPercentMargin,
      price_percent_margin: params.pricePercentMargin,
      lat_lng: [params.lat, params.lng],
      sub_category: Object.keys(params.category).map(key => ALL[key].value),
      transaction_type: 'Venda',
      radius: params.radius,
      amenities:
        (params.amenities && Object.keys(params.amenities)) || undefined,
      geometries:
        (params.geometries?.length > 0 &&
          params.geometries.map(item =>
            item
              .getPath()
              .getArray()
              .map(item => [item.lat(), item.lng()]),
          )) ||
        undefined,
    }

    if (params.recentOnly) payload.recent_only = params.recentOnly

    if (payload.geometries) {
      let fixedGeom = [...payload.geometries]
      if (
        fixedGeom[0][0].toString() !==
        fixedGeom[0][payload.geometries[0].length - 1].toString()
      ) {
        fixedGeom[0].push(fixedGeom[0][0])
      }
      payload.geometries = fixedGeom
    }

    if (params.sqrMeterPrice) payload.sqr_meter_price = params.sqrMeterPrice
    const tempPayload = workaroundForPayloadProcessing(params)
    tempPayload.bedrooms.length !== 0
      ? (payload.bedrooms = tempPayload.bedrooms)
      : null
    tempPayload.suites.length !== 0
      ? (payload.suites = tempPayload.suites)
      : null
    tempPayload.garages.length !== 0
      ? (payload.garages = tempPayload.garages)
      : null
    params.main_bedrooms
      ? (payload.main_bedrooms = String(params.main_bedrooms))
      : null
    params.main_suites
      ? (payload.main_suites = String(params.main_suites))
      : null
    params.main_garages
      ? (payload.main_garages = String(params.main_garages))
      : null

    if (params.alpha && params.beta) {
      payload.proximity_distances = [
        { distance: params.alpha, proximity_level: 'Alpha' },
        { distance: params.beta, proximity_level: 'Beta' },
        { distance: Number.MAX_SAFE_INTEGER, proximity_level: 'Gama' },
      ]
    }

    return payload
  }

  formatSample = sample => {
    if (!sample || sample[0]?.address) return sample
    const today = moment()
    return sample.map(item => {
      const {
        amenities,
        area,
        broker_raw,
        city_name,
        city_uf,
        created_at,
        features_bedroom,
        features_garage,
        features_suite,
        is_private,
        link,
        link_key,
        neighborhood_name,
        processed_address_building_name,
        processed_address_formatted,
        proximity_level,
        sqrmeter_price_area_sale,
        sub_category_name,
        total_area,
        transaction_sale,
        similarity,
      } = item

      const addressArray = processed_address_formatted
        .split(',')
        .map(item => item.trim())
      const ad_age = today.diff(moment(created_at), 'days')

      const similarity_color = !similarity
        ? 'red'
        : similarity < 75
        ? 'darkkhaki'
        : 'green'

      return {
        id: link_key,
        transaction_type: 'Venda',
        ad_age,
        area,
        total_area,
        amenities,
        created_at,
        similarity,
        similarity_color,
        ad_url: link,
        address: {
          city: city_name,
          district: neighborhood_name,
          number: addressArray[1],
          state: city_uf,
          street: addressArray[0],
        },
        bedrooms: features_bedroom,
        building: processed_address_building_name,
        category: sub_category_name,
        garages: features_garage,
        is_private,
        price: transaction_sale,
        proximity: proximity_level,
        real_state_agency: broker_raw,
        sqr_mtr_price: sqrmeter_price_area_sale,
        suites: features_suite,
      }
    })
  }

  formatCities = cities => {
    if (!cities || cities[0].id) return cities
    return cities.map(item =>
      item.PropriedadesEEmovel
        ? {
            boundbox: item.boundbox,
            stateId: item.EstadoId,
            id: item.Id,
            shore: item.IsLitoranea,
            name: `${item.Nome} - ${item.uf}`,
            asciiName: item.NomeASCI,
            lat: item.PontoCentralLatitude,
            lng: item.PontoCentralLongitude,
            active: item.PropriedadesEEmovel.AtuacaoPlataforma,
            capital: item.PropriedadesEEmovel.Capital,
            zoning: item.PropriedadesEEmovel.Zoneamento,
            zoom: item.Zoom,
            coordinates: item.coordinates,
            uf: item.uf,
          }
        : null,
    )
  }

  errorHandler = async (error, callback) => {
    let response = null
    if (error.response.status === 401) {
      await refreshToken()
      response = callback && (await callback(this.id && this.id))
      this.id = null
    }
    return response
  }

  formatFullAddress = valuation => {
    let fullAddress = ''
    let addressComponents = [
      'address_street',
      'address_number',
      'address_district',
      'address_city',
      'address_state',
    ]
    addressComponents.forEach((field, id) => {
      if (valuation[field]) {
        if (id < 4) fullAddress += `${valuation[field]}, `
        else fullAddress += valuation[field]
      }
    })
    return fullAddress
  }
}

export default ValuationService
